import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { fetchJson } from '../../../helpers/fetch.js';
import { stripEmail } from './useProjectResourceGrid.jsx';
import UserTreeSelect from '../../common/UserSelect/UserTreeSelect.jsx';
import VirtualSelectMenuList from '../../common/VirtualSelectMenuList.jsx';

import {
  common_own_direct_reports_path
} from '~/routes';

const SPECIAL_OPTION = {
  value: '__own_direct_reports__',
  label: I18n.t('select_own_direct_reports'),
  no_icon: true
};

const NO_DIRECT_REPORTS = {
  value: '__no_direct_reports__',
  label: I18n.t('no_direct_reports'),
  no_icon: true
};

const MultiValue = ({ data, getValue }) => {
  const selected = getValue();

  if (data !== selected[0]) return null;

  const labels = selected.slice(0, 2).map((item) => stripEmail(item.label));

  const moreCount = selected.length - 2;

  return (
    <span>
      {labels.join(', ')}
      {moreCount > 0 &&
        `, ${I18n.t('count_more', { count: moreCount })}`}
    </span>
  )
}

export const usePersonFilter = ({ enabled }) => {
  const [personIds, setPersonIds] = useState([]);
  const [directReportsSelected, setDirectReportsSelected] = useState(false);
  const queryClient = useQueryClient();

  const filterModel = useMemo(() => {
    if (!personIds.length && !directReportsSelected) return {};

    return {
      person_id: {
        filterType: 'set',
        values: personIds.map((item) => String(item).replace('-user', '')),
      },
    };
  }, [personIds, directReportsSelected]);

  const onPersonIdsChange = async (value) => {
    const hasOwnDirectReports = value?.[value.length - 1] === SPECIAL_OPTION.value;

    if (!hasOwnDirectReports) {
      setDirectReportsSelected(hasOwnDirectReports);
      setPersonIds(value?.length ? [value[value.length - 1]] : []);
      return;
    }

    const directReports = await queryClient.fetchQuery({
      queryKey: ['ownDirectReports'],
      queryFn: () => fetchJson(common_own_direct_reports_path()),
      staleTime: 5 * 60 * 1000,
    });

    setDirectReportsSelected(hasOwnDirectReports);
    setPersonIds(directReports);
  };

  const noneSelected = directReportsSelected && personIds.length === 0;
  const providedValue = useMemo(() => noneSelected ? [NO_DIRECT_REPORTS.value] : personIds, [noneSelected, personIds]);

  const UserFilterButton = !enabled ? null :
    <UserTreeSelect
      truncateOptions={true}
      value={providedValue}
      onChange={onPersonIdsChange}
      additionalOptions={[
        SPECIAL_OPTION,
        ...(noneSelected ? [NO_DIRECT_REPORTS] : [])
      ]}
      placeholder={`${I18n.t('select_person')}...`}
      selectComponents={{
        MultiValue,
        MenuList: VirtualSelectMenuList
      }}
      isClearable
      isMulti
      backspaceRemovesValue={false}
      styles={{
        container: (base) => ({
          ...base,
          display: 'inline-block',
          width: 'auto',
          borderRadius: 'var(--btn-border-radius, 18px)'
        }),
        control: (base) => ({
          ...base,
          width: 'max-content',
          minWidth: 150,
        }),
        valueContainer: (base) => ({
          ...base,
          flex: '0 0 auto',
          width: 'auto',
          overflow: 'visible',
        }),
        indicatorsContainer: (base) => ({
          ...base,
          flexShrink: 0,
        }),
        clearIndicator: (base) => ({
          ...base,
          padding: 0,
        }),
        option: (base) => ({
          ...base,
          width: 350,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }),
        menu: (base) => ({
          ...base,
          width: 'auto',
          minWidth: '100%',
          maxWidth: 400,
          zIndex: 1001,
        }),
      }}
    />;

  return {
    UserFilterButton,
    filterModel
  }
}